import { ISymbol } from '@webtrader/core/api/market/symbols/index';
import { ETradeFillPolicy } from '@webtrader/core/api/trade';
import request from '@webtrader/core/request';

export enum EMarginMode {
  MARGIN_CALC_FOREX,
  MARGIN_CALC_CFD,
  MARGIN_CALC_FUTURES,
  MARGIN_CALC_CFDINDEX,
  MARGIN_CALC_CFDLEVERAGE,
  TRADE_MODE_FOREX_NO_LEVERAGE,
  TRADE_MODE_EXCH_STOCKS = 32,
  TRADE_MODE_EXCH_FUTURES = 33,
  TRADE_MODE_EXCH_FORTS = 34,
  TRADE_MODE_EXCH_OPTIONS = 35,
  TRADE_MODE_EXCH_OPTIONS_MARGIN = 36,
  TRADE_MODE_EXCH_BONDS = 37,
  TRADE_MODE_EXCH_STOCKS_MOEX = 38,
  TRADE_MODE_EXCH_BONDS_MOEX = 39,
  TRADE_MODE_SERV_COLLATERAL = 64
}

export enum ETradeMode {
  TRADE_NO,
  TRADE_CLOSE,
  TRADE_FULL,
  TRADE_LONG,
  TRADE_SHORT
}

export interface ITradingSession {
  dayOfWeek: number;
  openDay: number;
  openTime: string;
  closeDay: number;
  closeTime: string;
}

export interface ITradingEpochSessions {
  dayOfWeek: number;
  tradingStartTime: number;
  tradingEndTime: number;
}

export enum ESymbolFillPolicy {
  NONE,
  FOK,
  IOC,
  ALL
}

export interface ITradeFillPolicy {
  marketFillPolicy?: ETradeFillPolicy[];
  limitFillPolicy?: ETradeFillPolicy[];
  stopFillPolicy?: ETradeFillPolicy[];
  stopLimitFillPolicy?: ETradeFillPolicy[];
}

export enum EProfitMode {
  PROFIT_CALC_FOREX = 0,
  PROFIT_CALC_CFD = 1,
  PROFIT_CALC_FUTURES = 2,
  PROFIT_CALC_CFDINDEX = 3,
  PROFIT_CALC_CFDLEVERAGE = 4,
  PROFIT_CALC_FOREX_NO_LEVERAGE = 5,
  PROFIT_CALC_EXCH_STOCKS = 32,
  PROFIT_CALC_EXCH_FUTURES = 33,
  PROFIT_CALC_EXCH_FORTS = 34,
  PROFIT_CALC_EXCH_OPTIONS = 35,
  PROFIT_CALC_EXCH_OPTIONS_MARGIN = 36,
  PROFIT_CALC_EXCH_BONDS = 37,
  PROFIT_CALC_EXCH_STOCKS_MOEX = 38,
  PROFIT_CALC_EXCH_BONDS_MOEX = 39,
  PROFIT_CALC_SERV_COLLATERAL = 64
}

export enum EExeMode {
  EXE_REQUEST,
  EXE_INSTANT,
  EXE_MARKET,
  EXE_EXCHANGE
}

export enum EGtcPendings {
  ORDERS_DAILY,
  ORDERS_GTC,
  ORDERS_DAILY_NO_STOPS
}

export enum ESwapType {
  SWAP_BY_POINTS,
  SWAP_BY_DOLLARS,
  SWAP_BY_INTEREST,
  SWAP_BY_MARGIN_CURRENCY,
  SWAP_DISABLED,
  SWAP_BY_SYMBOL_CURRENCY,
  SWAP_BY_GROUP_CURRENCY,
  SWAP_BY_INTEREST_CURRENT,
  SWAP_BY_INTEREST_OPEN,
  SWAP_REOPEN_BY_CLOSE_PRICE,
  SWAP_REOPEN_BY_BID,
  SWAP_BY_PROFIT_CURRENCY
}

export enum ESwapRollover3Days {
  MON = 1,
  TUE,
  WED,
  THUR,
  FRI,
  SAT,
  SUN
}

export interface ISymbolDetail extends ISymbol {
  digits: number;
  enable: boolean;
  exeMode: EExeMode;
  popular: boolean;
  closable: boolean;
  currency: string;
  profitMode: EProfitMode;
  gtcPendings: number;
  marginMode: EMarginMode;
  marginHedged: number;
  marginCurrency: string;
  marginPercentage: number;
  marginInitial: number;
  marginMaintenance: number;
  swapLong: number;
  swapType: ESwapType;
  swapShort: number;
  swapRollover3days: ESwapRollover3Days;
  tradeMode: ETradeMode;
  tradingSessions: ITradingSession[];
  tradingEpochSessions: ITradingEpochSessions[];

  // for order use
  pointStep: number;
  stopsLevel: number;
  lotSizeStep: number;
  contractSize: number;
  minContractLotSize: number;
  maxContractLotSize: number;

  // Centroid
  onePipEqual: number;

  // for shares
  revenue?: string;
  profile?: string;
  industry?: string;
  indexName?: string;
  netIncome?: string;
  totalAssets?: string;
  totalEquity?: string;
  operatingIncome?: string;

  symbolFillPolicy?: ESymbolFillPolicy;
  tradeFillPolicy?: ITradeFillPolicy;
  tradable?: boolean;
  tradableExeMode?: boolean;
  exchangeRate?: number;
  quoteCurrencyExchangeRate?: number;

  tickSize: number;
  tickValue: number;
}

interface ISymbolDetailParams {
  symbol: string;
}

const symbolDetail = (params: ISymbolDetailParams) =>
  request.get<ISymbolDetail>('/api/market/v1/symbol/detail', {
    params
  });

export default symbolDetail;
